// Lib
import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { ChainedCommands, Editor } from '@tiptap/react';

// Components

// Selectors
import { selectIsMobileAppMode } from '../../../../../../platform/platformSelector';
import { userLocalePreferenceSelector } from '../../../../../../user/currentUserSelector';

// Hooks
import { dangerouslyUseTiptapActiveEditor } from '../../../../../../components/tiptapEditor/store/tiptapEditorStoreHooks';

// Components
import TextStylePopupContent from './TextStylePopupContent';
import TiptapTextColorButton from './TiptapTextColorButton';
import TiptapTextHighlightButton from './TiptapTextHighlightButton';
import TextStyleEntry from './TextStyleEntry';
import Icon from '../../../../../../components/icons/Icon';

// Utils
import { getElementDefaultColors } from './textStylePopupUtils';

// Constants
import { wrapWithLocaleDoubleQuotes } from '../../../../../../../common/utils/i18n/quotationStyleUtils';
import { VALID_MILANOTE_TEXT_COLORS } from '../../../../../../../common/tiptap/extensions/TextColor';
import { MILANOTE_HIGHLIGHT_COLORS } from '../../../../../../components/editor/plugins/textColor/textColorConstants';

// Types
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { useTranslator } from '../../../../../../components/i18n/useTranslator';

const STYLED_TEXT_BLOCK_TYPES = ['heading', 'smallText', 'codeBlock', 'blockquote', 'callout'];
const MILANOTE_TEXT_COLORS = [...VALID_MILANOTE_TEXT_COLORS];

const isNormalTextActive = (activeEditor?: Editor | null) =>
    !activeEditor || STYLED_TEXT_BLOCK_TYPES.every((type) => !activeEditor.isActive(type));

interface TiptapTextStylePopupContentProps {
    element: ImMNElement;
    showBlockStyleTools: boolean;
}

/**
 * Unset any styles that are currently active (and won't be replaced by the new style).
 */
const unsetActiveWraps = (activeEditor: Editor): ChainedCommands => {
    let currentOperation = activeEditor.chain();

    if (activeEditor.isActive('codeBlock')) {
        currentOperation = currentOperation.toggleCodeBlock();
    }

    if (activeEditor.isActive('blockquote')) {
        currentOperation = currentOperation.unsetBlockquote();
    }

    if (activeEditor.isActive('callout')) {
        currentOperation = currentOperation.unsetCallout();
    }

    return currentOperation;
};

/**
 * Unsets any existing block styles, then runs a chained command function & optionally focuses the editor.
 */
const createChainRunner =
    (activeEditor: Editor | undefined, isMobileMode: boolean) =>
    (chainFn: (chain: ChainedCommands) => ChainedCommands) =>
    () => {
        if (!activeEditor) return;

        let chain: ChainedCommands = unsetActiveWraps(activeEditor);

        chain = chainFn(chain);

        // We don't want to focus on mobile mode because it will cause the virtual keyboard to open.
        // This would interfere with the sheet
        return isMobileMode ? chain.run() : chain.focus().run();
    };

const TiptapTextStylePopupContent = (props: TiptapTextStylePopupContentProps): ReactElement => {
    const { showBlockStyleTools, element } = props;
    const { defaultTextColor, defaultBackgroundColor } = getElementDefaultColors(element) || {};

    // This is running within a popup, so performance is not an issue and using the active
    // editor means we don't have to define individual selectors for each active state
    const activeEditor = dangerouslyUseTiptapActiveEditor();

    const isMobileMode = useSelector(selectIsMobileAppMode);
    const userLocalePreference = useSelector(userLocalePreferenceSelector);

    const runChainedCommand = createChainRunner(activeEditor, isMobileMode);

    const translate = useTranslator('toolbar', 'textStyles');

    /**
     * When selecting a text style, we need to handle the special case of blockquote
     * because it wraps the existing nodes instead of replacing them.
     */
    const onTextStyleSelect = (nodeType: string, attributes?: Record<string, unknown>) =>
        runChainedCommand((chain) => chain.setNode(nodeType, attributes));

    /**
     * When selecting a blockquote, we need special handling for lists.
     */
    const onBlockquoteSelect = runChainedCommand((chain) => chain.toggleBlockquote());

    /**
     * When converting to code blocks, we need special handling to combine multiple
     * lines into a single code block.
     */
    const onCodeBlockSelect = runChainedCommand((chain) => chain.toggleCodeBlock());

    /**
     * When converting to code blocks, we need special handling to combine multiple
     * lines into a single code block.
     */
    const onCalloutSelect = runChainedCommand((chain) => chain.toggleCallout());

    return (
        <TextStylePopupContent
            showBlockStyleTools={showBlockStyleTools}
            blockStyleComponents={
                <>
                    <TextStyleEntry
                        active={activeEditor?.isActive('heading', { level: 1 })}
                        blockClassname="header-one"
                        shortcut={['command', 'shift', '1']}
                        onTextStyleSelect={onTextStyleSelect('heading', { level: 1 })}
                    >
                        {translate('largeHeading')}
                    </TextStyleEntry>
                    <TextStyleEntry
                        active={activeEditor?.isActive('heading', { level: 2 })}
                        blockClassname="header-two"
                        shortcut={['command', 'shift', '2']}
                        onTextStyleSelect={onTextStyleSelect('heading', { level: 2 })}
                    >
                        {translate('normalHeading')}
                    </TextStyleEntry>
                    <TextStyleEntry
                        active={isNormalTextActive(activeEditor)}
                        shortcut={['command', 'shift', '0']}
                        onTextStyleSelect={onTextStyleSelect('paragraph')}
                    >
                        {translate('normalText')}
                    </TextStyleEntry>
                    <TextStyleEntry
                        active={activeEditor?.isActive('smallText')}
                        blockClassname="small-text"
                        shortcut={['command', 'shift', '9']}
                        onTextStyleSelect={onTextStyleSelect('smallText')}
                    >
                        {translate('smallText')}
                    </TextStyleEntry>
                    <TextStyleEntry
                        active={activeEditor?.isActive('codeBlock')}
                        blockClassname="code-block"
                        shortcut={['command', '>']}
                        onTextStyleSelect={onCodeBlockSelect}
                    >
                        {translate('codeBlock')}
                    </TextStyleEntry>
                    <TextStyleEntry
                        active={activeEditor?.isActive('callout')}
                        blockClassname="callout"
                        shortcut={['command', 'shift', '6']}
                        onTextStyleSelect={onCalloutSelect}
                    >
                        <Icon name="callout-warning" /> {translate('callout')}
                    </TextStyleEntry>
                    <TextStyleEntry
                        active={activeEditor?.isActive('blockquote')}
                        blockClassname="blockquote"
                        shortcut={['command', '"']}
                        onTextStyleSelect={onBlockquoteSelect}
                    >
                        {wrapWithLocaleDoubleQuotes(translate('quoteBlock'), userLocalePreference)}
                    </TextStyleEntry>
                </>
            }
            textColorComponents={
                <>
                    <TiptapTextColorButton
                        color={null}
                        customTextColor={defaultTextColor}
                        customBackgroundColor={defaultBackgroundColor}
                    />
                    {MILANOTE_TEXT_COLORS.map((color) => (
                        <TiptapTextColorButton
                            key={color}
                            color={color}
                            customBackgroundColor={defaultBackgroundColor}
                        />
                    ))}
                </>
            }
            textHighlightComponents={
                <>
                    {MILANOTE_HIGHLIGHT_COLORS.map((color) => (
                        <TiptapTextHighlightButton key={color} color={color} />
                    ))}
                </>
            }
        />
    );
};

export default TiptapTextStylePopupContent;
